import { cellCenter, geo, heroWalkable } from '../grid.ts';
import { cosA, dist2, iatan2, idiv, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, KneesRules, KneesState, PlatesRules, PlatesState, ShellRules, ShellState, TornoRules, TornoState } from '../types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { useSkills } from './ghost.ts';
import { gargOf } from '../battle/garg.ts';
import { KNEE_AXLES, KNEE_PILES, kneeOrder } from '../battle/knees.ts';
import { tornoAt, tornoOrder } from '../battle/torno.ts';
import { shellOrder } from '../battle/shell.ts';
import { plateFaceNow, plateSide } from '../battle/plates.ts';
import { shellWays, tornoWays, type ShellSet } from '../battle/tormo-layout.ts';

/**
 * The lab's bot for the artificer's chapter 5 (docs/sendas/artifice.md, «Tormo»; the common bot in ai/senda-bot.ts
 * hands these acts here). It only moves the hero, picks its target and taps the marks, as a player's commands would.
 *
 * - the knees (`knees`): who knows puts the gear on each joint's axle that touches two gears an odd number apart (the
 *   knees first), steps out of the stomp's ring (or in by his knees) and keeps the shades off Dunna; who doesn't tries
 *   the axles of each joint one by one.
 * - the winch (`torno`): who knows sets the box at the slowest the shelf gives (the small gears driving the big twice)
 *   and pulls; between gears it shoots the gargoyles off the cable. Who doesn't sets the obvious (the smallest at the
 *   motor, the biggest at the drum, the rest in between) and pulls again after every slip.
 * - the shell (`shell`, the test): who knows takes one stone a side, its way with the chest's drill counted (its ask,
 *   its belt backwards: the gang's clutch last, after the belts), waits out a twist close at hand and shoots the cubs at
 *   the drills. Who doesn't sees no chest's drill: clutches in first, then the belts, for whatever stone it can.
 * - Tormo (`plates`): who knows stands off on the glowing plate's side and strikes only from there, out of his fist,
 *   out of the stamp's ring and the hammer's warned, off the rocks; the cubs at Nadd when nobody stands before him.
 *   Who doesn't strikes Tormo from wherever it is.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const shoot = (s: BattleState, h: Hero, t: Enemy): void => {
  strike(s, h, t);
  h.forced = false;
};

const engage = (s: BattleState, h: Hero, t: Enemy): void => {
  if (withinRange(h.x, h.y, t.x, t.y, h.stats.range - 200)) return shoot(s, h, t);
  return strike(s, h, t);
};

const floorNear = (s: BattleState, x: number, y: number, r: number, ok: (p: { x: number; y: number }) => boolean = () => true): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  const R = s.map.radius;
  for (let c = 0, n = geo(R).q.length; c < n; c++) {
    if (!heroWalkable(s, c)) continue;
    const p = cellCenter(R, c);
    if (!withinRange(p.x, p.y, x, y, r) || !ok(p)) continue;
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

const offSpots = (s: BattleState, h: Hero, spots: { x: number; y: number; t: number }[], r: number): boolean => {
  const near = spots.filter((p) => withinRange(h.x, h.y, p.x, p.y, r + 250));
  if (!near.length) return false;
  const p = floorNear(s, h.x, h.y, r * 3, (c) => !spots.some((o) => withinRange(c.x, c.y, o.x, o.y, r + 400)));
  if (!p) return false;
  h.target = 0;
  go(s, h, p.x, p.y, 100);
  return true;
};

/** Out of a disc about to land round `x, y` (to its edge on the hero's side). True if it had to. */
const offDisc = (s: BattleState, h: Hero, x: number, y: number, r: number, inR = 0): boolean => {
  if (!withinRange(h.x, h.y, x, y, r + 300)) return false;
  if (inR > 0 && withinRange(h.x, h.y, x, y, inR - 250)) return false;
  const a = h.x === x && h.y === y ? 0 : iatan2(h.y - y, h.x - x);
  const d = r + 700;
  const p = floorNear(s, x + idiv(cosA(a) * d, 10000), y + idiv(sinA(a) * d, 10000), 1600, (c) => !withinRange(c.x, c.y, x, y, r + 350));
  if (!p) return false;
  h.target = 0;
  go(s, h, p.x, p.y, 100);
  return true;
};

/** Whether a point is clear of every spot warned (with room). */
const clearOf = (spots: { x: number; y: number }[], x: number, y: number, r: number): boolean => !spots.some((p) => withinRange(x, y, p.x, p.y, r + 400));

const npc = (s: BattleState, id: string): { x: number; y: number } | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

/* ---------- the knees ---------- */

export const kneesBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r: KneesRules | undefined = s.rules?.senda?.knees;
  const w: KneesState | undefined = s.senda?.knees;
  if (!r || !w || w.down >= 0) return;
  const working = w.job >= 0 && w.held > 0;
  if (knows) {
    // Out of the stomp's ring, unless the gear goes in before it lands.
    const finish = working && w.stompAt - s.tick > r.stand - w.held + 6;
    if (w.stompAt >= 0 && !finish && offDisc(s, h, w.x, w.y, r.stomp.outR, r.stomp.inR)) {
      w.job = -1;
      return;
    }
    if (!working && offSpots(s, h, w.spots, r.debris.r)) {
      w.job = -1;
      return;
    }
    // Shades at Dunna or at the hero first.
    const d = npc(s, 'dunna');
    const atD = d ? nearestTo(enemiesNear(s, d.x, d.y, 2400, (e) => targetable(s, e)), d.x, d.y) : null;
    const atMe = nearestTo(enemiesNear(s, h.x, h.y, 1800, (e) => targetable(s, e)), h.x, h.y);
    const t = atMe ?? atD;
    if (t && !working) {
      w.job = -1;
      return engage(s, h, t);
    }
  } else if (w.stompAt >= 0 && offDisc(s, h, w.x, w.y, r.stomp.outR)) {
    w.job = -1;
    return;
  }
  if (w.job >= 0) return;
  // A gear from the nearest heap first.
  if (!w.hand) {
    let best = 0;
    let bd = 0x7fffffff;
    r.piles.forEach((p, i) => {
      const dd = dist2(h.x, h.y, p.x, p.y);
      if (dd < bd) {
        bd = dd;
        best = i;
      }
    });
    return kneeOrder(s, h, KNEE_PILES + best);
  }
  for (const j of [0, 1, 2, 3]) {
    if (w.locked[j]) continue;
    const J = r.joints[j];
    if (!J) continue;
    const k = knows ? J.jam : J.free.findIndex((_, i) => !((w.tried[j] as number) & (1 << i)));
    if (k < 0) continue;
    // Who knows doesn't walk back into the ring before the stomp lands.
    if (knows && w.stompAt >= 0) return;
    return kneeOrder(s, h, j * KNEE_AXLES + k);
  }
};

/* ---------- the winch ---------- */

/** The box as wanted: the gear for each slot (A, B1, B2, C). */
const tornoWant = (r: TornoRules, knows: boolean): number[] => {
  const ways = tornoWays(r.pieces, r.input);
  if (knows) return (ways.find((x) => x.num * r.wantDen === r.wantNum * x.den) as { set: number[] }).set;
  // The obvious: the smallest at the motor, the biggest at the drum, the two left in between.
  const order = r.pieces.map((t, i) => ({ t, i })).sort((a, b) => a.t - b.t || a.i - b.i);
  const a = (order[0] as { i: number }).i;
  const c = (order[order.length - 1] as { i: number }).i;
  const mid = order.slice(1, -1).map((x) => x.i);
  return [a, mid[0] as number, mid[1] as number, c];
};

export const tornoBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r: TornoRules | undefined = s.rules?.senda?.torno;
  const w: TornoState | undefined = s.senda?.torno;
  if (!r || !w || w.end >= 0) return;
  const working = w.job >= 0 && w.held > 0;
  if (!working && offSpots(s, h, w.spots, r.rocks.r)) {
    w.job = -1;
    return;
  }
  // The gargoyles on the cable (and the ones at the hero) first, but not with a gear half put.
  if (!working) {
    const perched = s.enemies.filter((e) => e.hp > 0 && gargOf(s, e.id)?.mode === 1 && targetable(s, e));
    const atMe = nearestTo(enemiesNear(s, h.x, h.y, 1400, (e) => targetable(s, e)), h.x, h.y);
    const t = atMe ?? (knows && (w.hand < 0 || perched.length >= 2) ? nearestTo(perched, h.x, h.y) : null);
    if (t && withinRange(h.x, h.y, t.x, t.y, h.stats.range - 100)) {
      w.job = -1;
      return shoot(s, h, t);
    }
    // Out of reach on the cable: to the ledge's edge under it (not after it into the wall).
    if (t && !atMe) {
      const p = floorNear(s, t.x, t.y, 6000);
      if (p && !withinRange(h.x, h.y, p.x, p.y, 400)) {
        w.job = -1;
        h.target = 0;
        return go(s, h, p.x, p.y, 300);
      }
    }
  }
  if (w.job >= 0) return;
  const want = tornoWant(r, knows);
  // Not back under a stone warned before it falls (it shoots meanwhile).
  const order = (at: number): void => {
    const c = cellCenter(s.map.radius, at < 4 ? (r.slots[at] as number) : at < 4 + r.pieces.length ? (r.lie[at - 4] as number) : r.lever);
    if (!clearOf(w.spots, c.x, c.y, r.rocks.r)) {
      const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e)), h.x, h.y);
      if (t) shoot(s, h, t);
      return;
    }
    tornoOrder(s, h, at);
  };
  // Hands: the gear held goes to its slot (or back to its shelf if it isn't wanted).
  if (w.hand >= 0) {
    const slot = want.indexOf(w.hand);
    if (slot >= 0) return order(slot);
    return order(4 + w.hand);
  }
  for (let slot = 0; slot < 4; slot++) {
    const g = want[slot] as number;
    if (tornoAt(w, slot) === g) continue;
    // A wrong gear in the slot comes out first; else the wanted one is fetched.
    if (tornoAt(w, slot) >= 0) return order(slot);
    return order(w.on[g] as number);
  }
  // The box as wanted: pull (who knows once; who doesn't again after every slip).
  if (w.haul < 0) return order(4 + r.pieces.length);
  const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e)), h.x, h.y);
  if (t) return shoot(s, h, t);
  h.target = 0;
};

/* ---------- the shell ---------- */

const shellSetOf = (r: ShellRules): ShellSet => ({ force: r.drills.map((d) => d.force), right0: r.drills.map((d) => d.right0), gang: r.gang, need: r.need, reach: r.reach });

/** One step toward a way (drills out, belts, drills in; who knows the gang's last). True if it tapped something. */
const shellStep = (s: BattleState, h: Hero, r: ShellRules, w: ShellState, way: { inn: number; belts: number[] }, knows: boolean): boolean => {
  const outs = [0, 1, 2, 3].filter((d) => w.inn[d] && !(way.inn & (1 << d)));
  const ins = [0, 1, 2, 3].filter((d) => !w.inn[d] && way.inn & (1 << d)).sort((a, b) => (knows ? (a === r.gang ? 1 : 0) - (b === r.gang ? 1 : 0) : a - b));
  const levers = [0, 1].filter((k) => w.belts[k] !== way.belts[k]);
  if (outs.length) {
    shellOrder(s, h, outs[0] as number);
    return true;
  }
  // Who knows turns the belts before the clutches go in; who doesn't, after.
  if (knows && levers.length) {
    shellOrder(s, h, 4 + (levers[0] as number));
    return true;
  }
  if (ins.length) {
    shellOrder(s, h, ins[0] as number);
    return true;
  }
  if (levers.length) {
    shellOrder(s, h, 4 + (levers[0] as number));
    return true;
  }
  return false;
};

export const shellBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r: ShellRules | undefined = s.rules?.senda?.shell;
  const w: ShellState | undefined = s.senda?.shell;
  if (!r || !w || w.end >= 0) return;
  const working = w.job >= 0 && w.held > 0;
  if (w.twistAt >= 0 && offDisc(s, h, r.tormoAt.x, r.tormoAt.y, r.twist.sweepR)) {
    w.job = -1;
    return;
  }
  if (!working && offSpots(s, h, w.spots, r.shards.r)) {
    w.job = -1;
    return;
  }
  if (!working) {
    // The cubs at a drill in (or at the hero) first.
    const atMe = nearestTo(enemiesNear(s, h.x, h.y, 1500, (e) => targetable(s, e)), h.x, h.y);
    const cub = knows ? nearestTo(s.enemies.filter((e) => e.hp > 0 && w.cubs.includes(e.id) && targetable(s, e)), h.x, h.y) : null;
    const t = atMe ?? cub;
    if (t) {
      w.job = -1;
      return engage(s, h, t);
    }
  }
  if (w.job >= 0) return;
  const set = shellSetOf(r);
  const stones = [0, 1, 2].filter((k) => (w.down[k] as number) < 0);
  // Who knows waits out a twist about to come (the cut would start over).
  const soon = w.twistAt >= 0 || w.twist - s.waveT < r.cut + 36;
  const pick = (blind: boolean): { inn: number; belts: number[] } | null => {
    for (const k of stones) {
      const ways = shellWays(set, w.side, k, r.mill, r.chestForce, blind);
      if (!ways.length) continue;
      // The way with fewest taps from here.
      let best = ways[0] as { inn: number; belts: number[] };
      let bc = 99;
      for (const x of ways) {
        let c = 0;
        for (let d = 0; d < 4; d++) if (!!w.inn[d] !== !!(x.inn & (1 << d))) c++;
        for (let b = 0; b < 2; b++) if (w.belts[b] !== x.belts[b]) c++;
        if (c < bc) {
          bc = c;
          best = x;
        }
      }
      return best;
    }
    return null;
  };
  // Who doesn't: everything that reaches a stone still on goes in (more force, the obvious), the belts for the first of each.
  const blindWay = (): { inn: number; belts: number[] } => {
    const reach = r.reach[w.side] as number[];
    let inn = 0;
    const belts = [w.belts[0] as number, w.belts[1] as number];
    const set0 = [false, false];
    for (let d = 0; d < 4; d++) {
      const k = reach[d] as number;
      if (k < 0 || (w.down[k] as number) >= 0) continue;
      inn |= 1 << d;
      const b = d < 2 ? 0 : 1;
      if (!set0[b]) {
        set0[b] = true;
        belts[b] = r.drills[d]?.right0 ? 0 : 1;
      }
    }
    return { inn, belts };
  };
  const way = knows ? (soon || w.spots.length ? null : pick(false)) : blindWay();
  if (way && way.inn && shellStep(s, h, r, w, way, knows)) return;
  const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e)), h.x, h.y);
  if (t) return shoot(s, h, t);
  h.target = 0;
};

/* ---------- Tormo ---------- */

export const platesBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r: PlatesRules | undefined = s.rules?.senda?.plates;
  const w: PlatesState | undefined = s.senda?.plates;
  if (!r || !w || w.free >= 0 || w.broke >= 0) return;
  const e = findEnemy(s, w.id);
  if (!e) return;
  if (!knows) {
    if (w.hammer >= 0 && offDisc(s, h, e.x, e.y, r.hammer.r)) return;
    const near = nearestTo(enemiesNear(s, h.x, h.y, 1500, (x) => targetable(s, x) && x.id !== e.id), h.x, h.y);
    return engage(s, h, near ?? e);
  }
  // Out of what is warned: the hammer, the stamp's ring, the fist, the rocks.
  if (w.hammer >= 0 && offDisc(s, h, e.x, e.y, r.hammer.r)) return;
  if (w.stampAt >= 0 && offDisc(s, h, e.x, e.y, r.stamp.r)) return;
  if (w.fist >= 0 && offDisc(s, h, w.fistAt.x, w.fistAt.y, r.fist.r)) return;
  if (offSpots(s, h, w.spots, r.rocks.r)) return;
  // Cubs at Nadd with nobody before him; any at the hero.
  const nadd = npc(s, 'nadd');
  const front = npc(s, 'quarry') ?? npc(s, 'adela');
  const atNadd = nadd && !front ? nearestTo(enemiesNear(s, nadd.x, nadd.y, 2600, (x) => targetable(s, x) && x.id !== e.id), nadd.x, nadd.y) : null;
  const atMe = nearestTo(enemiesNear(s, h.x, h.y, 1200, (x) => targetable(s, x) && x.id !== e.id), h.x, h.y);
  const cub = atMe ?? atNadd;
  if (cub) return engage(s, h, cub);
  // The plate's side, a little off: out of his fist, in the hero's reach.
  const face = plateFaceNow(e, w);
  const d = r.fist.reach + 900;
  const want = { x: e.x + idiv(cosA(face) * d, 10000), y: e.y + idiv(sinA(face) * d, 10000) };
  const on = plateSide(r, w, e, h.x, h.y) && withinRange(h.x, h.y, e.x, e.y, h.stats.range - 150);
  if (on && !withinRange(h.x, h.y, e.x, e.y, r.fist.reach + 200)) return shoot(s, h, e);
  h.target = 0;
  const p = floorNear(s, want.x, want.y, 2200, (c) => plateSide(r, w, e, c.x, c.y) && !withinRange(c.x, c.y, e.x, e.y, r.fist.reach + 300));
  if (p) go(s, h, p.x, p.y, 400);
};
